import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLiffWithMember } from '../../hooks/useLiffWithMember';
import { useUserDetail } from '../../hooks/queries';
import { getTenantIdFromUrl } from '../../api/apiUtils';
import { useTenant } from '../../contexts/TenantContext';
import { LiffLoginGate } from '../../components/LiffLoginGate';
import { MemberInfo } from '../../components/MemberInfo';
import { PointsInfo } from '../../components/PointsInfo';
import { VouchersBlock } from '../../components/VouchersBlock';
import { PackagesBlock } from '../../components/PackagesBlock';
import { OrderHistory } from '../../components/OrderHistory';
import { Loading } from '../../components/Loading';

interface UserData {
  id: string;
  name: string;
  phone: string | null;
  lineId: string | null;
  birthday: string | null;
  points: number;
  pointsYuanEquivalent: number;
  createdAt: string;
}

/** SWR/API 錯誤常見欄位 */
type ErrLike = {
  status?: number;
  response?: { status?: number };
  message?: string;
};

/**
 * 會員中心應用程式
 */
function getLoginRedirectUri(): string {
  return (
    window.location.origin + window.location.pathname + window.location.search
  );
}

export function MemberApp() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenantId: contextTenantId } = useTenant();
  const urlTenantId = getTenantIdFromUrl();
  const effectiveTenantId = contextTenantId || urlTenantId || null;

  const {
    isReady,
    profile,
    isLoggedIn,
    shouldUseLiff,
    error: liffError,
    ensureMember: ensureData,
    isMemberLoading: isLoadingEnsure,
    ensureError,
    mutateEnsure,
    liff,
  } = useLiffWithMember();

  const userId = ensureData?.id;

  // 取得使用者詳細資訊
  const {
    user: userDetail,
    isLoading: isLoadingDetail,
    error: detailError,
    mutate: mutateUserDetail,
  } = useUserDetail(userId, {
    enabled: !!userId,
  });

  // 處理導航（當資料載入完成時）
  useEffect(() => {
    if (userDetail) {
      const returnTo = searchParams.get('returnTo');
      if (returnTo) {
        // 將除 returnTo 以外的 query 一併帶到目標路由（例如 transactionId、orderId、tenantId）
        const extraParams = new URLSearchParams(searchParams);
        extraParams.delete('returnTo');
        const decodedReturnTo = decodeURIComponent(returnTo);
        const extraQuery = extraParams.toString();
        const targetPath =
          extraQuery.length > 0
            ? `${decodedReturnTo}${
                decodedReturnTo.includes('?') ? '&' : '?'
              }${extraQuery}`
            : decodedReturnTo;
        setTimeout(() => {
          navigate(targetPath, { replace: true });
        }, 100);
      }
    }
  }, [userDetail, searchParams, navigate]);

  // 從響應中提取使用者資料；若 GET /customers/:id 無回傳但已有 ensureData，用 ensureData 作為 fallback 避免卡在載入
  const userData: UserData | null = (() => {
    const from =
      userDetail ?? (ensureData && !isLoadingDetail ? ensureData : null);
    if (!from) return null;
    return {
      id: String(from.id),
      name: (from as { name?: string }).name ?? '',
      phone: (from as { phone?: string | null }).phone ?? null,
      lineId: (from as { line_id?: string | null }).line_id ?? null,
      birthday: (from as { birthday?: string | null }).birthday ?? null,
      points: Number((from as { points?: number }).points ?? 0),
      pointsYuanEquivalent: Number(
        (from as { points_yuan_equivalent?: number }).points_yuan_equivalent ??
          0
      ),
      createdAt:
        (from as { created_at?: string }).created_at ??
        new Date().toISOString(),
    };
  })();

  const loading = isLoadingEnsure || isLoadingDetail;
  const error = (() => {
    if (liffError) {
      return liffError instanceof Error
        ? liffError
        : new Error(String(liffError));
    }
    if (ensureError) {
      return ensureError instanceof Error
        ? ensureError
        : new Error(String(ensureError));
    }
    if (detailError) {
      const err = detailError as ErrLike | Error;
      return err instanceof Error
        ? err
        : new Error((err as ErrLike)?.message || '取得使用者詳細資訊失敗');
    }
    return null;
  })();

  return (
    <LiffLoginGate
      isReady={isReady}
      shouldUseLiff={shouldUseLiff}
      isLoggedIn={isLoggedIn}
      error={error}
      onLoginClick={() => liff?.login?.({ redirectUri: getLoginRedirectUri() })}
    >
      {!isReady || loading ? (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loading />
        </div>
      ) : isReady && !loading && !ensureData && !effectiveTenantId ? (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              無法取得店家資訊
            </h2>
            <p className="text-gray-600 mb-4">
              請從原活動或預購連結重新進入會員中心，或關閉此頁後由 LINE
              選單進入。
            </p>
            <button
              type="button"
              onClick={() => window.close()}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
            >
              關閉
            </button>
          </div>
        </div>
      ) : ensureData && !userData ? (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              無法載入會員資料
            </h2>
            <p className="text-gray-600 mb-4">請稍後再試或重新整理</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg"
            >
              重新載入
            </button>
          </div>
        </div>
      ) : !userData ? (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loading />
        </div>
      ) : (
        <div className="min-h-screen bg-gray-50 py-4 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="mb-6 text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                會員資訊
              </h1>
              <p className="text-sm text-gray-600">歡迎，{userData.name}</p>
            </div>

            <MemberInfo
              customerId={userData.id}
              name={userData.name}
              phone={userData.phone}
              lineId={userData.lineId}
              birthday={userData.birthday}
              createdAt={userData.createdAt}
              onUpdated={() => {
                void mutateEnsure();
                void mutateUserDetail?.();
              }}
            />

            <PointsInfo
              points={userData.points}
              pointsYuanEquivalent={userData.pointsYuanEquivalent}
            />

            <VouchersBlock customerId={userData.id} />
            <PackagesBlock customerId={userData.id} />

            <OrderHistory
              userId={userData.id}
              highlightOrderNumber={
                searchParams.get('highlightOrder') || undefined
              }
            />
          </div>
        </div>
      )}
    </LiffLoginGate>
  );
}
